/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
/* eslint-disable no-console */
import * as Faye from 'faye';
import { authenticatedRequest } from '../../common/authenticatedRequest';
import { EventHandler, Subscription } from '../types';
import { diagnostics } from '@utils/diagnostics';

interface IWebSocketClient {
  getClient(): any;
  subscribe<T>(channelUrl: string, handler: EventHandler<T>): Subscription | undefined;
  initialize(namespace: string, websocketUrl: string, authUrl: string): void;
}

const request = authenticatedRequest(() => {
  window.location.href = `/api/internal/auth/login?redirectUrl=${window.location.pathname}`;
});

export class FayeClient implements IWebSocketClient {
  private client: typeof Faye.Client;
  private fayeNamespace = '';

  getClient() {
    return this.client;
  }

  initialize(fayeNamespace: string, fayeUrl: string, authUrl: string) {
    this.fayeNamespace = fayeNamespace;
    this.client = new Faye.Client(fayeUrl, {
      timeout: 60,
      retry: 5,
    });

    this.client.bind('transport:down', () => {
      console.log('[CONNECTION DOWN]');
    });

    this.client.bind('transport:up', () => {
      console.log('[CONNECTION UP]');
    });

    this.client.addExtension({
      async outgoing(msg: any, cb: any) {
        if (!msg.ext) {
          msg.ext = {};
        }

        if (msg.channel !== '/meta/subscribe') {
          // Ignore non-subscription requests
          return cb(msg);
        }

        // Fetch faye token when a new channel subscription is requested
        // This is required to authenticate the websocket connection
        try {
          const res = await request.post(authUrl, { subscription: msg.subscription });
          msg.ext.webToken = res.data.webToken;
          cb(msg);
        } catch (err: any) {
          if (err.response) {
            const res = err.response;
            if (res.status === -1) {
              // Special case -1 for e2e tests.
              diagnostics.info(`Request to get websocket cancelled; subscription: ${msg.subscription}`);
            } else if (res.status === 401) {
              // Don't error for unauthenticated users, usually due to session timeout
              diagnostics.info('User not authenticated while fetching faye token');
              return;
            } else {
              diagnostics.error(new Error('Request to fetch faye token failed'), res.data);
            }
          } else {
            diagnostics.error(new Error('Error while fetching faye token'), err);
          }
          cb();
        }
      },
    });
  }

  subscribe<T>(channelUrl: any, handler: EventHandler<T>): Subscription | undefined {
    const url = `/${this.fayeNamespace}${channelUrl}`;

    try {
      const subscription = this.client.subscribe(url, handler);
      console.log(`[SUBSCRIBE SUCCEEDED: ${url}]`);
      return subscription;
    } catch (error) {
      // todo: handle session timeout 401 errors
      diagnostics.error(new Error('Faye subscribe failed.'), { fayeUrl: url, error });
    }
    return;
  }
}
